<template>
  <section class="page">
    <h2 class="page__title">{{ $t('chpo.plan.title') }}</h2>

    <div class="page__table">
      <DataTable
        :columns="localizedColumns"
        :rows="tableRows"
        :row-key="(r) => r.__key || r.id"
        :row-height="64"
        :row-is-section="(r) => !!r.__section"
        :row-height-by-row="(r) => r.__section ? 56 : (r.editing ? null : 64)"
      >

        <template #head-composition>
          <div class="tbl__header-text">{{ $t('chpo.plan.headComposition') }}</div>
        </template>
        <template #cell-composition="{ row }">
          <div v-if="row.__section" class="section-row">
            <h3 class="section-row__title">{{ sectionLabel(row.__section) }}</h3>
            <button
              v-if="canEditPlan"
              class="section-row__add"
              type="button"
              @click="addRowToSection(row.__section)"
              :title="$t('chpo.plan.addRowTitle')"
            >+</button>
          </div>
          <template v-else>
            <div v-if="!row.editing" class="cell-text">
              <template v-if="row.composition && row.composition.length">
                <span v-for="(pid, idx) in row.composition" :key="pid + '-' + idx">
                  <span>{{ personAbbr(pid) }}</span><span v-if="idx < row.composition.length - 1">, </span>
                </span>
              </template>
              <span v-else class="text-muted">—</span>
            </div>

            <div v-else class="reinforce-picker">
              <template v-if="showComposition(row)">
                <div v-if="mpoConflictsInRow(row).length" class="mpo-alert">
                  {{ $t('chpo.plan.mpoConflict', { list: mpoConflictsInRow(row).join('; ') }) }}
                </div>
                <GroupedMultiSelect
                  :options="compositionOptions"
                  :model-value="row.composition"
                  :show-full-labels="true"
                  :panel-mode="true"
                  :panel-title="$t('chpo.plan.compositionPickerTitle')"
                  :used-keys="usedCompositionKeysExcept(row)"
                  :placeholder="$t('chpo.plan.compositionPh')"
                  :search-placeholder="$t('chpo.plan.compositionSearchPh')"
                  @update:modelValue="onCompositionChange(row, $event)"
                />
              </template>
              <template v-else>
                <span class="text-muted">—</span>
              </template>
            </div>
          </template>
        </template>

        
        <template #head-dutyType>
          <div class="tbl__header-text">{{ $t('chpo.plan.headDutyType') }}</div>
        </template>
        <template #cell-dutyType="{ row }">
          <template v-if="!row.__section">
            <div v-if="!row.editing" class="cell-text">{{ row.dutyType || '—' }}</div>
            <!--
              По ТЗ 03.09.2026: SelectField рендерит <input type=text> с
              browser-дефолтным size≈20 (~150-200px min-content), поэтому
              grid-item (клетка 180px) не сжимает потомка — селект физически
              вылезал за границы колонки. `.cell-fit` — min-width:0 + overflow:
              hidden + width:100% — заставляет обёртку сжаться до фактической
              ширины ячейки и обрезает overflow.
            -->
            <div v-else class="cell-fit">
              <SelectField
                v-model="row.dutyType"
                :options="dutyTypeOptions"
                :searchable="false"
                :placeholder="$t('chpo.plan.dutyTypePh')"
                width="100%"
              />
            </div>
          </template>
        </template>

        
        <template #head-reinforcement>
          <div class="tbl__header-text">{{ $t('chpo.plan.headReinforcement') }}</div>
        </template>
        <template #cell-reinforcement="{ row }">
          <template v-if="!row.__section">
            <div v-if="!row.editing" class="cell-text">
              <template v-if="row.reinforcement && row.reinforcement.length">
                {{ reinforcementAbbrs(row).join(', ') }}
              </template>
              <span v-else class="text-muted">—</span>
            </div>
            <div v-else class="reinforce-picker">
              <template v-if="showReinforcement(row)">
                <!-- Если справочник ТМУ загружен — выбираем конкретное изделие из типа
                     через двухуровневый (Тип → Изделие) GroupedMultiSelect.
                     Иначе fallback на плоский MultiSelectField с абстрактными категориями. -->
                <GroupedMultiSelect
                  v-if="reinforcementGrouped.length"
                  :options="reinforcementGrouped"
                  :model-value="row.reinforcement"
                  :show-full-labels="true"
                  :placeholder="$t('chpo.plan.reinforcementGroupedPh')"
                  :search-placeholder="$t('chpo.plan.reinforcementSearchPh')"
                  @update:modelValue="onReinforcementChange(row, $event)"
                />
                <MultiSelectField
                  v-else
                  :model-value="row.reinforcement"
                  :options="reinforcementOptions"
                  width="75%"
                  @update:modelValue="onReinforcementChange(row, $event)"
                />
              </template>
              <template v-else>
                <span class="text-muted">—</span>
              </template>
            </div>
          </template>
        </template>

        
        <template #head-sector>
          <div class="tbl__header-text">{{ $t('chpo.plan.headSector') }}</div>
        </template>
        <template #cell-sector="{ row }">
          <!--
            По ТЗ 03.09.2026: улитка (B12-3) объединена с участком в одну
            колонку — иначе строка не помещалась и селект «Виды п/н» обрезался.
            В display: «квадрат — улитка», в edit — кнопка/список + инпут улитки под ним.
          -->
          <template v-if="!row.__section">
            <div v-if="!row.editing" class="cell-text sector-cell">
              <template v-if="Array.isArray(row.sectors) && row.sectors.length">
                {{ row.sectors.join(', ') }}
                <span v-if="row.ulitka" class="ulitka-badge">{{ row.ulitka }}</span>
              </template>
              <template v-else-if="row.ulitka">
                <span class="ulitka-badge">{{ row.ulitka }}</span>
              </template>
              <span v-else class="text-muted">—</span>
            </div>
            <div v-else class="sector-cell sector-cell--edit">
              <template v-if="showSector(row)">
                <div v-if="!Array.isArray(row.sectors) || !row.sectors.length">
                  <ActionButton type="button" :color="'#2A8A1B'" :text="$t('chpo.plan.selectSectorBtn')" @click="navigateToSectorPicker(row)" />
                </div>
                <div v-else class="sector-picked">
                  <span class="sector-text">{{ row.sectors.join(', ') }}</span>
                  <button type="button" class="btn-small" @click="navigateToSectorPicker(row)">{{ $t('chpo.plan.changeBtn') }}</button>
                </div>
                <input
                  class="tbl-input ulitka-input"
                  type="text"
                  v-model="row.ulitka"
                  :placeholder="$t('chpo.plan.ulitkaPh')"
                  maxlength="16"
                  @blur="row.ulitka = (row.ulitka || '').trim().toUpperCase()"
                  :title="$t('chpo.plan.ulitkaTitle')"
                />
              </template>
              <template v-else>
                <span class="text-muted">—</span>
              </template>
            </div>
          </template>
        </template>

        <template #head-time>
          <div class="tbl__header-text">{{ $t('chpo.plan.headTime') }}</div>
        </template>
        <template #cell-time="{ row }">
          <template v-if="!row.__section">
            <div v-if="!row.editing" class="cell-text">
              <template v-if="row.timeFrom && row.timeTo">{{ row.timeFrom }} - {{ row.timeTo }}</template>
              <span v-else class="text-muted">—</span>
            </div>
            <div v-else class="time-range">
              <template v-if="showTime(row)">
                <label class="time-field">
                  <span>{{ $t('chpo.plan.from') }}</span>
                  <TimePicker v-model="row.timeFrom" :minute-step="30" />
                </label>
                <label class="time-field">
                  <span>{{ $t('chpo.plan.to') }}</span>
                  <TimePicker v-model="row.timeTo" :minute-step="30" class="time-to" />
                </label>
              </template>
              <template v-else>
                <span class="text-muted">—</span>
              </template>
            </div>
          </template>
        </template>

        
        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <template v-if="!row.__section && canEditPlan">
            <div class="actions">
              <button v-if="!row.editing" class="actions__btn actions__btn--edit" @click="onEdit(row)" :title="$t('chpo.plan.editTitle')" :aria-label="$t('chpo.plan.editTitle')">
                <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
              </button>
              <template v-else>
                <button class="actions__btn actions__btn--accept" @click="onAccept(row)" :title="$t('chpo.plan.saveTitle')" :aria-label="$t('chpo.plan.saveTitle')">
                  <img class="actions__icon" src="../../assets/images/accept.svg" alt="accept" />
                </button>
                <button class="actions__btn actions__btn--delete" @click="onDeleteRow(row)" :title="$t('chpo.plan.deleteTitle')" :aria-label="$t('chpo.plan.deleteTitle')">
                  <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
                </button>
              </template>
            </div>
          </template>
        </template>
      </DataTable>
    </div>

    
    <div v-if="!canEditPlan" class="readonly-banner">
      <span v-html="readOnlyBannerHtml"></span>
    </div>
    <div class="page__footer">
      <div class="page__footer-right">
        <ActionButton :outlined="true" :borderColor="'#111827'" :text="$t('chpo.plan.backBtn')" @click="goBack" />
        <ActionButton v-if="canEditPlan" :color="'#2A8A1B'" :disabled="hasBlockingErrors" :text="$t('chpo.plan.saveAndScheduleBtn')" @click="saveSchedule" />
      </div>
    </div>


    <div class="warnings" v-if="allWarnings.length">
      <div class="warnings__title">{{ $t('chpo.plan.warningsTitle') }}</div>
      <ul class="warnings__list">
        <li v-for="(w, idx) in allWarnings" :key="'w-' + idx">{{ w }}</li>
      </ul>
    </div>

    
    <!-- Агрегация плана охраны (по ТЗ встречи 27.08.2026) -->
    <div class="aggregation" v-if="peopleAll.length">
      <div class="aggregation__title">{{ $t('chpo.plan.aggregationTitle') }}</div>
      <table class="aggregation__table">
        <thead>
          <tr>
            <th></th>
            <th>{{ $t('chpo.plan.aggOfficers') }}</th>
            <th>{{ $t('chpo.plan.aggContract') }}</th>
            <th>{{ $t('chpo.plan.aggConscript') }}</th>
            <th>{{ $t('chpo.plan.aggWorkers') }}</th>
            <th>{{ $t('chpo.plan.aggOthers') }}</th>
            <th class="aggregation__total">{{ $t('chpo.plan.aggTotal') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="aggregation__row-label">{{ $t('chpo.plan.aggByStaff') }}</td>
            <td>{{ aggregation.byStaff.officers }}</td>
            <td>{{ aggregation.byStaff.contract }}</td>
            <td>{{ aggregation.byStaff.conscript }}</td>
            <td>{{ aggregation.byStaff.workers }}</td>
            <td>{{ aggregation.byStaff.others }}</td>
            <td class="aggregation__total"><b>{{ aggregation.byStaff.total }}</b></td>
          </tr>
          <tr>
            <td class="aggregation__row-label">{{ $t('chpo.plan.aggByList') }}</td>
            <td>{{ aggregation.byList.officers }}</td>
            <td>{{ aggregation.byList.contract }}</td>
            <td>{{ aggregation.byList.conscript }}</td>
            <td>{{ aggregation.byList.workers }}</td>
            <td>{{ aggregation.byList.others }}</td>
            <td class="aggregation__total"><b>{{ aggregation.byList.total }}</b></td>
          </tr>
          <tr>
            <td class="aggregation__row-label">{{ $t('chpo.plan.aggInReserve') }}</td>
            <td>{{ aggregation.inReserve.officers }}</td>
            <td>{{ aggregation.inReserve.contract }}</td>
            <td>{{ aggregation.inReserve.conscript }}</td>
            <td>{{ aggregation.inReserve.workers }}</td>
            <td>{{ aggregation.inReserve.others }}</td>
            <td class="aggregation__total"><b>{{ aggregation.inReserve.total }}</b></td>
          </tr>
          <tr>
            <td class="aggregation__row-label">{{ $t('chpo.plan.aggOnReinforce') }}</td>
            <td>{{ aggregation.onReinforce.officers }}</td>
            <td>{{ aggregation.onReinforce.contract }}</td>
            <td>{{ aggregation.onReinforce.conscript }}</td>
            <td>{{ aggregation.onReinforce.workers }}</td>
            <td>{{ aggregation.onReinforce.others }}</td>
            <td class="aggregation__total"><b>{{ aggregation.onReinforce.total }}</b></td>
          </tr>
          <tr class="aggregation__row--highlight">
            <td class="aggregation__row-label">{{ $t('chpo.plan.aggOnDuty') }}</td>
            <td>{{ aggregation.onDuty.officers }}</td>
            <td>{{ aggregation.onDuty.contract }}</td>
            <td>{{ aggregation.onDuty.conscript }}</td>
            <td>{{ aggregation.onDuty.workers }}</td>
            <td>{{ aggregation.onDuty.others }}</td>
            <td class="aggregation__total"><b>{{ aggregation.onDuty.total }}</b></td>
          </tr>
        </tbody>
      </table>
      <div class="aggregation__hint">
        {{ $t('chpo.plan.compensationHint') }}
      </div>
    </div>

    <div class="not-included" v-if="notIncludedIds.length">
      <div class="not-included__title">
        {{ $t('chpo.plan.notIncludedTitle') }}
        <span class="not-included__count">{{ notIncludedIds.length }}</span>
      </div>
      <div class="not-included__list">
        <div class="not-included__item" v-for="pid in notIncludedIds" :key="'miss-' + pid">
          {{ personLabel(pid) }}
        </div>
      </div>
    </div>

    <transition name="toast-slide">
      <div v-if="showToast" class="toast" :class="toastMessage.includes('✗') ? 'toast-error' : 'toast-success'">
        <div class="toast-content">{{ toastMessage }}</div>
      </div>
    </transition>
  </section>
</template>

<script>
// TODO (API): пример импорта клиентских обёрток
// import { getPlan, savePlan, validatePlan, getDutyTypes, getReinforcementOptions, getSectors, getPersonnel } from '@/api/plan.js'

import DataTable from '@/components/table/DataTable.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import GroupedMultiSelect from '@/components/order/GroupedMultiSelect.vue'
import SelectField from '@/components/widgets/SelectField.vue'
import MultiSelectField from '@/components/widgets/MultiSelectField.vue'
import TimePicker from '@/components/widgets/TimePicker.vue'
import { checkOrCreateChpoByDate, computeChpoDate } from '@/api/chpo';
import { createChpoOrderToSquad, updateChpoOrderToSquad, deleteChpoOrderToSquad } from '@/api/chpoOrdersToSquads';
import { http, getRefresh, setAccess } from '@/api/http';
import { getLatestRelations } from '@/api/mpo';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { listDutyTypes } from '@/api/dutyTypes';
import { listBookUsers } from '@/api/bookUsers';
import { ensureTodayBookContext, checkOrCreateBookByDate, computeBookDate } from '@/api/book';
import { invalidateBookContext } from '@/api/http';
import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user';
import { canEdit } from '@/access/armAccess';
import { localizedName } from '@/utils/localizedName';

export default {
  name: 'PlanView',
  components: { DataTable, ActionButton, GroupedMultiSelect, SelectField, MultiSelectField, TimePicker },
  data() {
    return {
      // Текущий пользователь — для RBAC проверки права редактирования плана охраны
      currentUser: null,
      // По ТЗ 09.09.2026: чтобы «Итоги по подразделению» 5-б раздела совпадали
      // с 4 разделом КПС, тянем те же stats из book/check/ (bookContext.stats).
      // Раньше aggregation() считала эвристиками по rank_name / personal_number
      // и данные расходились (Офицеры 4 vs 11, На усилении 16 vs 0 и т.п.).
      bookStats: null,
      // По ТЗ 03.09.2026:
      //   • dutyType шире (селект «ПН — Пограничный наряд» не должен обрезаться);
      //   • отдельная колонка «Улитка» убрана — вводится/показывается прямо
      //     внутри «Участок несения службы» (см. #cell-sector).
      columns: [
        { key: 'composition',   labelKey: 'chpo.plan.headComposition',   width: 210, flex: true },
        { key: 'dutyType',      labelKey: 'chpo.plan.headDutyType',      width: 180 },
        { key: 'reinforcement', labelKey: 'chpo.plan.headReinforcement', width: 180 },
        { key: 'sector',        labelKey: 'chpo.plan.headSector',        width: 220 },
        { key: 'time',          labelKey: 'chpo.plan.headTime',          width: 190 },
        { key: 'actions',       label: '',                               width: 90 },
      ],

      sectionDefs: [
        { key: 'carry',   labelKey: 'chpo.plan.sectionCarry' },
        { key: 'current', labelKey: 'chpo.plan.sectionCurrent' },
        { key: 'sec3',    labelKey: 'chpo.plan.sectionSec3' },
        { key: 'sec4',    labelKey: 'chpo.plan.sectionSec4' },
        { key: 'sec5',    labelKey: 'chpo.plan.sectionSec5' },
      ],


      people: [
      ],


      // Дефолтный набор до загрузки с бэка; замещается справочником DutyType из API
      dutyTypes: ['Д', 'ЧГ', 'ЧЗ', 'ЧОО', 'ЧП', 'С', 'ПН', 'ПТН', 'РЛП', 'ПРП', 'ПП', 'КРП', 'РПГ', 'ТГ', 'ПГ', 'Зн', 'Зс', 'ПК', 'РГГ', 'ДСС', 'ДОТН', 'ДЖ', 'ПП', 'Выходной', 'Командировка', 'Отпуск'],
      dutyTypesFull: [],  // весь справочник объектами {id, code, name, requires_stpn}

      // Доп. виды
      // (добавляем в конец, чтобы не ломать привычный порядок)
      // Выходной / Командировка / Отпуск


      sectors: ['4л-4м', '5к', 'НСОУ-1', 'НСОУ-2', 'Парк', 'Склад'],


      reinforcementOptions: [
        { key: '-',        label: '-',                 abbr: '-', count: null },
        { key: 'dogs',     label: 'Служебные животные', abbr: 'СЖ', count: 4 },
        { key: 'radios',   label: 'Радиостанции',        abbr: 'РС', count: 2 },
        { key: 'radar',    label: 'Радиолокационные ст.',abbr: 'РЛС', count: 2 },
        { key: 'search',   label: 'Прожекторные ст.',    abbr: 'ПС', count: 1 },
        { key: 'pistols',  label: 'Сигнальные пистолеты',abbr: 'СП', count: 2 },
        { key: 'special',  label: 'Спец. средства',      abbr: 'СС', count: 4 },
        { key: 'cars',     label: 'Автотранспорт',       abbr: 'АТ', count: 5 },
        { key: 'optics',   label: 'Приборы наблюдения',  abbr: 'ПН', count: 5 },
        { key: 'uav',      label: 'БПЛА',                abbr: 'БПЛА', count: 3 },
        { key: 'tsog',     label: 'Датчики и ТСОГ',      abbr: 'ТСОГ', count: 8 },
      ],
      // Группированные по типу (ТМУ) варианты для GroupedMultiSelect.
      // Заполняется после подгрузки номенклатуры с бэка.
      reinforcementGrouped: [],


      rows: [
      ],


      loading: false,
      error: null,
      planDateISO: null,
      mpoRelations: { survey_id: null, classifications: {}, positive_pairs: [], negative_pairs: [] },

      toastMessage: '',
      showToast: false,
    }
  },
  computed: {
    peopleAll() { return this.people || [] },
    /**
     * Право редактировать план охраны. По заказу — только начальник заставы (НЗ).
     * Администратор видит план в read-only, но НЕ может его составлять.
     */
    canEditPlan() {
      return canEdit(this.currentUser || getCachedCurrentUser(), '/chpo/plan')
    },

    allWarnings() {
      const warnings = []
      this.findOverlaps().forEach(w => warnings.push(w))
      this.rows.forEach(r => {
        if (!r.__section && r.dutyType && this.requiresSTPN(r)) {
          const cnt = this.countSTPN(r)
          if (cnt < 1) warnings.push(this.$t('chpo.plan.warnStpnNeeded', { dt: r.dutyType, id: r.id }))
        }
      })
      return warnings
    },
    localizedColumns() {
      return this.columns.map(c => ({
        ...c,
        label: c.labelKey ? this.$t(c.labelKey) : (c.label || '')
      }))
    },
    readOnlyBannerHtml() {
      // Разделяем на «префикс — жирный роль», чтобы сохранить <b>
      const banner = this.$t('chpo.plan.readOnlyBanner')
      const role = this.$t('chpo.plan.chiefRole')
      return banner.replace(role, `<b>${role}</b>`)
    },
    hasBlockingErrors() {
      return this.findOverlaps().length > 0
    },

    notIncludedIds() {
      const included = new Set()
      this.rows.forEach(r => (r.composition || []).forEach(k => included.add(this.personId(k))))
      return this.peopleAll.map(p => p.id).filter(id => !included.has(id))
    },

    /**
     * По ТЗ 09.09.2026: агрегация 5-б раздела «Итоги по подразделению»
     * читает те же stats из bookContext, что и 4 раздел КПС «Силы и средства».
     * Раньше считала локальными эвристиками (rank regex, personal_number ≥ 41
     * → «на усилении»), из-за чего числа расходились (Офицеры 4 vs 11,
     * На усилении 16 vs 0). Теперь единый источник — book/views.py::stats.
     *
     * Маппинг stats → колонки таблицы:
     *   oficers   → officers
     *   contracts → contract
     *   srochniki → conscript
     *   workers   → other  (в 4 разделе это «Рабочие и служащие»)
     *
     * Строки:
     *   in_house   → По штату (active − reinforcement)
     *   in_book    → По списку (все active в книге)
     *   archived   → В расходе (уникальные с Absence на дату)
     *   helped     → Прибыло на усиление (active + is_reinforcement)
     *   total_used → Используется в охране границ (uniq из OrderToSquads)
     */
    aggregation() {
      const empty = { officers: 0, contract: 0, conscript: 0, other: 0, total: 0 }
      const rowFrom = (key) => {
        const s = this.bookStats || {}
        const o = s.oficers?.[key]   || 0
        const c = s.contracts?.[key] || 0
        const r = s.srochniki?.[key] || 0
        const w = s.workers?.[key]   || 0
        const oth = s.others?.[key]  || 0
        return { officers: o, contract: c, conscript: r, workers: w, others: oth, total: o + c + r + w + oth }
      }
      if (!this.bookStats) {
        return {
          byStaff: { ...empty }, byList: { ...empty },
          inReserve: { ...empty }, onReinforce: { ...empty },
          onDuty: { ...empty }, totalInUnit: 0,
        }
      }
      const byList = rowFrom('in_book')
      return {
        byStaff:     rowFrom('in_house'),
        byList,
        inReserve:   rowFrom('archived'),
        onReinforce: rowFrom('helped'),
        onDuty:      rowFrom('total_used'),
        totalInUnit: byList.total,
      }
    },

    tableRows() {
      // В таблицу отдаём ссылки на реальные строки this.rows, а не копии,
      // чтобы onEdit/onAccept и v-model работали с одной и той же сущностью
      const rowsBySection = (key) => this.rows.filter(r => this.effectiveSection(r) === key)
      const out = []
      this.sectionDefs.forEach(sec => {
        out.push({ __key: `sec-${sec.key}`, __section: sec.key })
        rowsBySection(sec.key).forEach(r => out.push(r))
      })
      return out
    },

    
    dutyTypeOptions() {
      // По ТЗ 03.09.2026: в узкой колонке 180px «Пограничный наряд» переносится
      // на 3 строки. Оставляем только аббревиатуру (ПН/ППП/Д…); расшифровка
      // остаётся во всплывающей подсказке `title`.
      const full = this.dutyTypesFull || []
      if (full.length) {
        return [
          { value: '-', label: '-' },
          ...full.map(dt => ({ value: dt.code, label: dt.code, title: this.dictName(dt) || dt.name })),
        ]
      }
      return [{ value: '-', label: '-' }, ...(this.dutyTypes || []).map(v => ({ value: v, label: String(v) }))]
    },
    
    compositionOptions() {
      // По ТЗ 02.09.2026: в панели «Выбор состава ПН» раньше отображались только
      // л/н без ФИО — офицер не мог понять кого выбирает. Теперь строим:
      //   abbr  = «звание № л/н» (короткий чип слева)
      //   label = «Фамилия И.О.» (полное имя ищется поиском)
      // Ключи кодируем префиксом S:/O: чтобы отличать СТПН-выбор от прочих.
      // СТПН — только с is_stpn=true; дата приказа проверяется отдельно на save.
      const shortRank = (r) => {
        if (!r) return ''
        const map = {
          'лейтенант': 'л-т', 'старший лейтенант': 'ст. л-т', 'капитан': 'к-н',
          'майор': 'м-р', 'подполковник': 'п/п-к', 'полковник': 'п-к',
          'младший сержант': 'мл. с-т', 'сержант': 'с-т', 'старший сержант': 'ст. с-т',
          'старшина': 'старш.', 'прапорщик': 'пр-к', 'старший прапорщик': 'ст. пр-к',
          'рядовой': 'ряд.', 'ефрейтор': 'ефр.',
        }
        const key = String(r).toLowerCase()
        return map[key] || String(r)
      }
      const shortFio = (full) => {
        if (!full) return ''
        const p = String(full).trim().split(/\s+/).filter(Boolean)
        if (p.length <= 1) return full
        const initials = p.slice(1).map(w => w[0] ? w[0] + '.' : '').join('')
        return `${p[0]} ${initials}`.trim()
      }
      const abbrOf = (p) => {
        const r = shortRank(p.rank_name)
        const pn = (p.personal_number != null && p.personal_number !== '') ? `№${p.personal_number}` : `#${p.id}`
        return r ? `${r} ${pn}` : pn
      }
      const labelOf = (p) => {
        const fio = shortFio(p.full_name || p.abbr || '')
        return fio || String(p.personal_number || p.id)
      }
      const toStpnOpt  = (p) => ({ key: `S:${p.id}`, abbr: abbrOf(p), label: labelOf(p) })
      const toOtherOpt = (p) => ({ key: `O:${p.id}`, abbr: abbrOf(p), label: labelOf(p) })
      const stpnPeople = this.peopleAll.filter(p => !!p.is_stpn)
      const otherPeople = this.peopleAll.filter(p => !p.is_stpn)
      return [
        { label: this.$t('chpo.plan.compGroupStpn'), options: stpnPeople.map(toStpnOpt) },
        { label: this.$t('chpo.plan.compGroupOther'), options: otherPeople.map(toOtherOpt) },
      ]
    },
  },
  async mounted() {
    // Синхронно подгружаем юзера — от его роли зависит доступность редактирования.
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
    // TODO (API): загрузить данные плана на выбранную дату
    this.initLoad();
    this.loadReinforcementFromNomenclature();
    this.loadDutyTypesFromServer();
    this.applySelectedSectorFromRoute()
  },
  activated() {
    
    this.applySelectedSectorFromRoute()
  },
  watch: {
    '$route.query.sectorSelected': function () {
      this.applySelectedSectorFromRoute()
    },
    '$route.query.sectorsSelected': function () {
      this.applySelectedSectorFromRoute()
    },
  },
  beforeDestroy() {
    clearTimeout(this.__toastTimer)
    clearTimeout(this.__redirectTimer)
  },
  methods: {
    dictName(entity) {
      return localizedName(entity, this.$i18n.locale)
    },
    async loadDutyTypesFromServer() {
      try {
        const list = await listDutyTypes()
        if (Array.isArray(list) && list.length) {
          this.dutyTypesFull = list
          this.dutyTypes = list.map(dt => dt.code)
        }
      } catch (e) {
        console.warn('PlanView: не удалось подгрузить справочник видов п/н', e)
      }
    },
    async loadReinforcementFromNomenclature() {
      try {
        const resp = await getNomenclatures({ page_size: 500 });
        const list = resp?.data?.results || resp?.data || [];
        if (!Array.isArray(list) || !list.length) return;

        const buildAbbr = (n) => {
          const src = String(n || '').trim();
          if (!src) return '';
          const parts = src.split(/\s+/).filter(Boolean);
          const initials = parts.map(p => p[0] || '').filter(c => /[A-Za-zА-Яа-я]/.test(c)).join('').toUpperCase();
          return initials.slice(0, 6) || src.slice(0, 4).toUpperCase();
        };

        // Плоский формат опций для MultiSelectField (для отображения).
        // Ключ id — конкретное изделие из ТМУ (nomenclature.id).
        const flat = list.map(item => ({
          key: item.id ?? item.code ?? item.name,
          label: this.dictName(item) || item.name || item.title || '—',
          abbr: item.code || item.short_name || buildAbbr(item.name),
          count: item.count ?? item.quantity ?? null,
          nomenclature_id: item.id,
          nomenclature_type: item.nomenclature_type,
          nomenclature_type_name: item.nomenclature_type_name || 'Прочее',
        }));

        this.reinforcementOptions = [
          { key: '-', label: '-', abbr: '-', count: null },
          ...flat,
        ];

        // Группируем по типу (ТМУ) для показа в выпадашке составом «тип → конкретное изделие».
        const groups = new Map();
        for (const opt of flat) {
          const g = opt.nomenclature_type_name || 'Прочее';
          if (!groups.has(g)) groups.set(g, []);
          groups.get(g).push(opt);
        }
        this.reinforcementGrouped = Array.from(groups.entries()).map(([label, options]) => ({
          label,
          options,
        }));
      } catch (e) {
        console.warn('PlanView: не удалось подгрузить номенклатуру', e);
      }
    },
    parsePersonKey(key) {
      if (typeof key === 'number') return { kind: 'N', id: key }
      const s = String(key || '')
      const m = s.match(/^([SO]):(\d+)$/)
      if (m) return { kind: m[1], id: Number(m[2]) }
      const n = Number(s)
      return Number.isFinite(n) ? { kind: 'N', id: n } : { kind: 'N', id: null }
    },
    personId(key) {
      return this.parsePersonKey(key).id
    },
    normalizeCompositionKeys(keys) {
      const picked = Array.isArray(keys) ? keys : []
      const byId = new Map() // id -> kind (prefer S)
      for (const k of picked) {
        const { kind, id } = this.parsePersonKey(k)
        if (!id) continue
        const prev = byId.get(id)
        if (!prev) byId.set(id, kind)
        else if (prev !== 'S' && kind === 'S') byId.set(id, 'S')
      }
      const stpn = []
      const other = []
      for (const [id, kind] of byId.entries()) {
        if (kind === 'S') stpn.push(`S:${id}`)
        else other.push(`O:${id}`)
      }
      return [...stpn, ...other]
    },
    showSuccessToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 2500)
    },
    showErrorToast(message) {
      this.toastMessage = message
      this.showToast = true
      clearTimeout(this.__toastTimer)
      this.__toastTimer = setTimeout(() => { this.showToast = false }, 3500)
    },
    normalizeDutyType(v) {
      return String(v || '').trim()
    },
    isLeaveDutyType(v) {
      const dt = this.normalizeDutyType(v)
      return dt === 'Выходной' || dt === 'Командировка' || dt === 'Отпуск'
    },
    requiresSTPN(row) {
      // Для Выходной/Командировка/Отпуск СТПН не обязателен
      return !this.isLeaveDutyType(row?.dutyType)
    },
    makeRow(partial = {}) {
      // Стандартное время наряда: с 20:00 до 08:00 следующих суток.
      // Переопределяется через partial (при загрузке существующих нарядов).
      const r = {
        id: partial.id ?? Date.now(),
        carryOver: !!partial.carryOver,
        dutyType: partial.dutyType || '',
        reinforcement: partial.reinforcement ? [...partial.reinforcement] : [],
        sectors: partial.sectors ? [...partial.sectors] : [],
        ulitka: partial.ulitka || '',
        timeFrom: partial.timeFrom || '20:00',
        timeTo: partial.timeTo || '08:00',
        composition: partial.composition ? [...partial.composition] : [],
        editing: false,
        section: partial.section || null,
        reinforcementText: partial.reinforcementText || '',
        orderType: partial.orderType || '',
      }
      r.compositionSet = new Set(r.composition)
      r.firstAllowed = () => true
      r.firstAllowedMessageHidden = false
      return r
    },

    addRow() {
      const nextId = (this.rows.reduce((m, r) => Math.max(m, r.id), 0) || 0) + 1
      const row = this.makeRow({ id: nextId })
      row.editing = true
      row.__new = true
      this.rows = [...this.rows, row]
    },
    addRowToSection(sectionKey) {
      const nextId = (this.rows.reduce((m, r) => Math.max(m, r.id), 0) || 0) + 1
      const payload = { id: nextId }
      if (sectionKey === 'carry') payload.carryOver = true
      else if (sectionKey === 'current') payload.carryOver = false
      else payload.section = sectionKey
      const row = this.makeRow(payload)
      row.editing = true
      row.__new = true
      this.rows = [...this.rows, row]
    },

    // Все поля наряда доступны для редактирования сразу — без каскадного показа.
    // (для «отпуск/командировка/выходной» скрываем то, что не имеет смысла)
    showReinforcement(row) {
      if (!row.editing) return false
      if (this.isLeaveDutyType(row.dutyType)) return false
      return true
    },
    showSector(row) {
      if (!row.editing) return false
      if (this.isLeaveDutyType(row.dutyType)) return false
      return true
    },
    showTime(row) {
      if (!row.editing) return false
      if (this.isLeaveDutyType(row.dutyType)) return false
      return true
    },
    showComposition(row) {
      if (!row.editing) return false
      return true
    },

    isTimeFilled(row) {
      const s = this.parseTime(row.timeFrom), e = this.parseTime(row.timeTo)
      return s != null && e != null && e !== s
    },

    /**
     * Возвращает список ключей (S:id / O:id / id) всех людей, которые УЖЕ
     * заняты в других (не текущей) строках плана. В боковой панели выбора
     * состава эти люди подсвечиваются оранжевым — визуальный сигнал что
     * человек в этом же плане уже задействован.
     */
    usedCompositionKeysExcept(currentRow) {
      const used = new Set()
      for (const r of this.rows) {
        if (r === currentRow) continue
        if (r.__section) continue
        for (const k of (r.composition || [])) used.add(k)
      }
      return Array.from(used)
    },
    onCompositionChange(row, newVal) {
      if (!this.requiresSTPN(row)) {
        row.firstAllowedMessageHidden = true
        row.composition = this.normalizeCompositionKeys(newVal)
        return
      }
      const normalized = this.normalizeCompositionKeys(newVal)
      const stpnCount = normalized.filter(k => this.parsePersonKey(k).kind === 'S').length
      // Если СТПН не выбрали явно — просто считаем первым выбранным как СТПН.
      if (stpnCount < 1 && normalized.length) {
        const firstId = this.personId(normalized[0])
        const rest = normalized.slice(1).map(k => `O:${this.personId(k)}`)
        row.firstAllowedMessageHidden = true
        row.composition = [`S:${firstId}`, ...rest]
        return
      }
      row.firstAllowedMessageHidden = stpnCount > 0
      row.composition = normalized
    },

    onReinforcementChange(row, newVal) {
      const arr = Array.isArray(newVal) ? [...newVal] : []
      if (arr.includes('-')) {
        row.reinforcement = ['-']
        return
      }
      row.reinforcement = arr
    },

    onEdit(row) {
      row.__backup = JSON.parse(JSON.stringify(row))
      row.editing = true
      row.__new = false // при редактировании существующей строки порядок шагов не применяем
    },
    onAccept(row) {
      this.saveRow(row)
    },
    async saveRow(row) {
      row.compositionSet = new Set(row.composition || [])
      const isLeave = this.isLeaveDutyType(row.dutyType)
      const compIds = (row.composition || []).map(k => this.personId(k)).filter(Boolean)
      const chief = isLeave ? null : (compIds[0] || null)
      const users = isLeave ? compIds : compIds.slice(1)
      if (!isLeave && chief && this.requiresSTPN(row)) {
        const chiefPerson = this.peopleAll.find(p => p.id === chief)
        const planDate = this.planDateISO || null
        if (!chiefPerson?.is_stpn) {
          this.showErrorToast(this.$t('chpo.plan.errStpnNoSign'))
          return
        }
        if (!chiefPerson.sentry_leader_date) {
          this.showErrorToast(this.$t('chpo.plan.errStpnNoOrder'))
          return
        }
        if (planDate && chiefPerson.sentry_leader_date > planDate) {
          this.showErrorToast(this.$t('chpo.plan.errStpnOrderNotEffective'))
          return
        }
      }
      const reinforcementKeys = Array.isArray(row.reinforcement) && row.reinforcement.length
        ? [...row.reinforcement]
        : this.parseReinforcementKeys(row.reinforcementText)
      const payload = {
        chief_id: chief,
        users_ids: users,
        squad_type: row.dutyType || '',
        order_type: this.computeOrderType(row),
        reinforcement_means: reinforcementKeys.join(', '),
        sector: (row.sectors || []).join(', '),
        ulitka: (row.ulitka || '').trim().toUpperCase().slice(0, 16),
        start_time: (this.parseTime(row.timeFrom) == null || isLeave) ? null : this.toHHMMSS(row.timeFrom || ''),
        end_time: (this.parseTime(row.timeTo) == null || isLeave) ? null : this.toHHMMSS(row.timeTo || ''),
      }
      try {
        if (row.__new) {
          const created = await this.tryWithRefresh(() => createChpoOrderToSquad(payload));
          row.id = created.id;
        } else {
          await this.tryWithRefresh(() => updateChpoOrderToSquad(row.id, payload));
        }
        // Синхронизация локального состояния после сохранения
        row.reinforcement = [...reinforcementKeys];
        row.reinforcementText = reinforcementKeys.join(', ');
        row.timeFrom = this.toHHMM(row.timeFrom);
        row.timeTo = this.toHHMM(row.timeTo);
        row.orderType = payload.order_type;
        row.editing = false; row.__new = false; delete row.__backup;
      } catch (e) {
        // откат
        if (row.__backup) Object.assign(row, row.__backup);
        row.editing = false;
        this.error = e?.message || String(e);
      }
    },
    async tryWithRefresh(fn) {
      try {
        return await fn();
      } catch (e) {
        const is401 = e && (e.status === 401 || /unauthor/i.test(e?.message || '') || /unauthor/i.test(e?.payload?.detail || ''));
        if (is401) {
          try {
            const refresh = getRefresh();
            if (refresh) {
              const r = await http.post('users/token/refresh/', { refresh });
              const newAccess = r?.data?.access; if (newAccess) setAccess(newAccess);
              return await fn();
            }
          } catch (_) {}
        }
        throw e;
      }
    },
    async onDeleteRow(row) {
      if (!row.__new) {
        try {
          await this.tryWithRefresh(() => deleteChpoOrderToSquad(row.id))
        } catch (e) {
          // Если запись уже отсутствует на backend (404/DoesNotExist) — просто убираем из UI.
          const msg = String(e?.message || '')
          if (e?.status !== 404 && !msg.includes('No OrderToSquads matches')) {
            alert(this.$t('chpo.plan.errDeletePrefix', { msg: (e.message || e) }))
            return
          }
        }
      }
      this.rows = this.rows.filter(r => r.id !== row.id)
    },

    async initLoad() {
      this.loading = true; this.error = null;
      try {
        const dateISO = computeChpoDate();
        this.planDateISO = dateISO;
        const ctx = await checkOrCreateChpoByDate(dateISO);

        // Личный состав берём из раздела 2 КПС (список военнослужащих и работников)
        // — это авторитетный список для данной заставы. Fallback на CHPOUser
        // сохранён на случай, если по каким-то причинам BookContext недоступен.
        let peopleLoaded = false;
        try {
          // По ТЗ 14.09.2026: инвалидируем кэш, чтобы 5-б брал те же live-данные,
          // что и 4 раздел. Иначе после чистки дублей BookUser здесь остаются
          // старые числа.
          invalidateBookContext(computeBookDate());
          const bookCtx = await ensureTodayBookContext();
          // Сохраняем stats из bookContext — используется в aggregation()
          // (5-б раздел «Итоги по подразделению»), чтобы числа совпадали с 4 разделом.
          this.bookStats = bookCtx?.stats || null;
          const bookUsersRaw = await listBookUsers({ is_archived: false });
          // По ТЗ 02.09.2026: /api/book/users/ до фикса фильтра возвращал N×книг
          // одинаковых юзеров — дедуплицируем по user.id, чтобы СТПН/Остальные
          // считались по уникальным лицам, а не сотнями дубликатов.
          const seenUserIds = new Set()
          const bookUsers = (Array.isArray(bookUsersRaw) ? bookUsersRaw : []).filter(bu => {
            const uid = (bu.user && bu.user.id) || bu.user_id || bu.id
            if (!uid || seenUserIds.has(uid)) return false
            seenUserIds.add(uid); return true
          })
          if (bookUsers.length) {
            const mapped = bookUsers.map((bu) => {
              // может прийти либо flat-объект, либо nested {user: {...}}
              const u = bu.user || bu;
              return {
                id: u.user_id || u.id || bu.user_id,
                personal_number: u.personal_number ?? bu.personal_number ?? '',
                abbr: u.full_name || bu.fio || bu.full_name || '',
                full_name: u.full_name || bu.fio || bu.full_name || '',
                position_name: u.position?.name || u.position_name || bu.position || null,
                rank_name: u.rank?.name || u.rank_name || bu.rank || null,
                group: 'ALL',
                is_stpn: !!(u.is_stpn ?? bu.is_stpn),
                sentry_leader_date: u.sentry_leader_date || bu.sentry_leader_date || null,
                stpn_type: u.stpn_type || null,
              };
            }).filter(x => x.id);
            if (mapped.length) { this.people = mapped; peopleLoaded = true; }
          }
        } catch (e) {
          console.warn('PlanView: не удалось подгрузить список из раздела 2 КПС, использую CHPOUser', e);
        }

        if (!peopleLoaded && Array.isArray(ctx?.users)) {
          const mapped = ctx.users.map((u) => ({
            id: u.user.id,
            personal_number: u.user.personal_number ?? '',
            abbr: u.user.full_name || u.user.name || String(u.user.id),
            full_name: u.user.full_name || '',
            group: 'ALL',
            is_stpn: !!u.user.is_stpn,
            sentry_leader_date: u.user.sentry_leader_date || null,
            stpn_type: u.user.stpn_type || null,
          }));
          if (mapped.length) this.people = mapped;
        }
        // Загрузка нарядов (orders_to_squads)
        this.rows = (ctx?.orders_to_squads || []).map(sq => this.mapSquadToRow(sq));
        // Социометрические связи (МПО) для бейджей и предупреждений в составе наряда
        try {
          this.mpoRelations = await getLatestRelations();
        } catch (_) {
          // не критично — план охраны работает и без МПО
        }
      } catch (e) {
        this.error = e?.message || String(e);
      } finally {
        this.loading = false;
      }
    },
    mpoConflictsInRow(row) {
      const negativePairs = this.mpoRelations?.negative_pairs || []
      if (!negativePairs.length) return []
      const idsInRow = new Set((row.composition || []).map(k => this.personId(k)).filter(Boolean))
      const conflicts = []
      for (const [a, b] of negativePairs) {
        if (idsInRow.has(a) && idsInRow.has(b)) {
          conflicts.push(`№${this.personAbbr(a)} ↔ №${this.personAbbr(b)}`)
        }
      }
      return conflicts
    },
    mapSquadToRow(sq) {
      const chiefId = sq?.chief?.id
      const userIds = (sq?.users || []).map(u => u.id).filter(id => id !== chiefId)
      const composition = chiefId ? [`S:${chiefId}`, ...userIds.map(id => `O:${id}`)] : userIds.map(id => `O:${id}`)
      const reinKeys = this.parseReinforcementKeys(sq?.reinforcement_means)
      return this.makeRow({
        id: sq.id,
        dutyType: sq.squad_type || '',
        reinforcement: reinKeys,
        sectors: this.parseSectors(sq?.sector),
        ulitka: sq.ulitka || '',
        timeFrom: this.toHHMM(sq.start_time || ''),
        timeTo: this.toHHMM(sq.end_time || ''),
        composition,
        section: null,
        reinforcementText: (sq.reinforcement_means || '').trim(),
        orderType: sq.order_type || '',
      })
    },

    reinforcementAbbrs(row) {
      if (row.reinforcement && row.reinforcement.length) {
        return (row.reinforcement || []).map(k => this.reinforcementOptions.find(o => o.key === k)?.abbr || k);
      }
      if (row.reinforcementText) {
        const keys = this.parseReinforcementKeys(row.reinforcementText);
        return keys.map(k => this.reinforcementOptions.find(o => o.key === k)?.abbr || k);
      }
      return [];
    },
    navigateToSectorPicker(row) {
      const back = this.$route.fullPath
      const sectors = Array.isArray(row.sectors) ? row.sectors : []
      this.$router.push({ name: 'sector-select', query: { rowId: String(row.id), back, sectorsSelected: sectors.join(',') } })
    },
    applySelectedSectorFromRoute() {
      const { sectorSelected, sectorsSelected, rowId } = this.$route.query || {}
      if ((!sectorSelected && !sectorsSelected) || !rowId) return
      const id = Number(rowId)
      const row = this.rows.find(r => r.id === id)
      if (row) {
        if (!Array.isArray(row.sectors)) row.sectors = []
        const codes = []
        if (Array.isArray(sectorsSelected)) {
          sectorsSelected.forEach(s => typeof s === 'string' && codes.push(s))
        } else if (typeof sectorsSelected === 'string' && sectorsSelected) {
          codes.push(...sectorsSelected.split(',').map(s => s.trim()).filter(Boolean))
        } else if (typeof sectorSelected === 'string' && sectorSelected) {
          codes.push(sectorSelected.trim())
        }
        codes.forEach(s => {
          if (!row.sectors.includes(s)) row.sectors.push(s)
        })
      }
      const { sectorSelected: _s, sectorsSelected: _ss, rowId: _r, ...rest } = this.$route.query
      this.$router.replace({ query: { ...rest } })
    },

    parseSectors(str) {
      if (!str || typeof str !== 'string') return []
      return str.split(',').map(s => s.trim()).filter(Boolean)
    },

    parseTime(t) {
      if (!t || !/^[0-2]\d:[0-5]\d$/.test(t)) return null
      const [h, m] = t.split(':').map(Number)
      if (h > 23) return null
      return h * 60 + m
    },
    intervalSegments(fromStr, toStr) {
      const s = this.parseTime(fromStr)
      const e = this.parseTime(toStr)
      if (s == null || e == null) return []
      if (e === s) return []
      if (e > s) return [[s, e]]
      return [[s, 1440], [0, e]]
    },
    segOverlap(a, b) { return a[0] < b[1] && b[0] < a[1] },
    findOverlaps() {
      const res = []
      
      const perPerson = new Map() // id -> [{ rowId, segs }]
      this.rows.forEach(r => {
        const segs = this.intervalSegments(r.timeFrom, r.timeTo)
        if (!segs.length) return
        ;(r.composition || []).forEach(k => {
          const pid = this.personId(k)
          if (!pid) return
          if (!perPerson.has(pid)) perPerson.set(pid, [])
          perPerson.get(pid).push({ rowId: r.id, segs })
        })
      })
      
      perPerson.forEach((arr, pid) => {
        for (let i = 0; i < arr.length; i++) {
          for (let j = i + 1; j < arr.length; j++) {
            const a = arr[i], b = arr[j]
            if (a.rowId === b.rowId) continue
            if (a.segs.some(s1 => b.segs.some(s2 => this.segOverlap(s1, s2)))) {
              res.push(this.$t('chpo.plan.warnOverlap', { p: this.personAbbr(pid), a: a.rowId, b: b.rowId }))
            }
          }
        }
      })
      return res
    },
    findRestViolations(minMinutes) {
      const res = []
      const perPerson = new Map() // id -> [{ rowId, from, to }]
      this.rows.forEach(r => {
        const s = this.parseTime(r.timeFrom), e = this.parseTime(r.timeTo)
        if (s == null || e == null || s === e) return
        ;(r.composition || []).forEach(k => {
          const pid = this.personId(k)
          if (!pid) return
          if (!perPerson.has(pid)) perPerson.set(pid, [])
          perPerson.get(pid).push({ rowId: r.id, from: s, to: e })
        })
      })
      const day = 1440
      perPerson.forEach((arr, pid) => {
        
        for (let i = 0; i < arr.length; i++) {
          for (let j = 0; j < arr.length; j++) {
            if (arr[i].rowId === arr[j].rowId) continue
            const gap = (arr[j].from - arr[i].to + day) % day
            if (gap > 0 && gap < minMinutes) {
              res.push(this.$t('chpo.plan.warnRest', { p: this.personAbbr(pid), a: arr[i].rowId, b: arr[j].rowId, gap }))
            }
          }
        }
      })
      return res
    },
    personAbbr(key) {
      const id = this.personId(key)
      if (id == null) return ''
      const p = this.peopleAll.find(x => x.id === id)
      if (p && p.personal_number != null && p.personal_number !== '') return String(p.personal_number)
      return String(id)
    },
    personLabel(key) {
      // Развёрнутый вид для блока "не включенные": № — ФИО (должность)
      const id = this.personId(key)
      if (id == null) return ''
      const p = this.peopleAll.find(x => x.id === id)
      if (!p) return String(id)
      const pn = p.personal_number != null && p.personal_number !== '' ? `№${p.personal_number}` : ''
      const name = p.full_name || p.abbr || ''
      const pos = p.position_name ? ` (${p.position_name})` : ''
      return [pn, name].filter(Boolean).join(' — ') + pos
    },
    countSTPN(row) {
      return (row.composition || []).filter(k => this.parsePersonKey(k).kind === 'S').length
    },
    async saveSchedule() {
      const overlaps = this.findOverlaps()
      if (overlaps.length) {
        this.showErrorToast(this.$t('chpo.plan.toastCantSaveOverlap'))
        clearTimeout(this.__redirectTimer)
        return
      }

      const editingRows = (this.rows || []).filter(r => !r.__section && r.editing)
      if (editingRows.length) {
        this.showErrorToast(this.$t('chpo.plan.toastCantSaveUnsaved'))
        clearTimeout(this.__redirectTimer)
        return
      }

      // После сохранения плана — автоматически создаём переходящие наряды
      // на завтрашний день (это те, что пересекают полночь — end < start
      // или заканчиваются в 24:00 / 00:00 следующего дня).
      try {
        const created = await this.createCarryOverForTomorrow()
        if (created > 0) {
          this.showSuccessToast(this.$t('chpo.plan.toastSavedWithCarry', { n: created }))
        } else {
          this.showSuccessToast(this.$t('chpo.plan.toastSaved'))
        }
      } catch (e) {
        console.warn('Не удалось создать переходящий наряд', e)
        this.showSuccessToast(this.$t('chpo.plan.toastSavedNoCarry'))
      }
      clearTimeout(this.__redirectTimer)

      // По ТЗ (встреча 27.08.2026): после сохранения → графический распорядок дня.
      this.__redirectTimer = setTimeout(() => {
        this.$router.push('/chpo/daily-schedule')
      }, 900)
    },
    /**
     * Определяет строки-кандидаты на «переходящий» наряд:
     * пересечение полуночи (end_time <= start_time, либо end_time = 24:00 / 00:00).
     */
    _isCrossMidnight(row) {
      const s = row.start_time || row.startTime || ''
      const e = row.end_time || row.endTime || ''
      if (!s || !e) return false
      const [sh, sm] = s.split(':').map(Number)
      const [eh, em] = e.split(':').map(Number)
      if (Number.isNaN(sh) || Number.isNaN(eh)) return false
      // 24:00 или 00:00 в графе end + любой start после 12:00 → почти всегда переходящий
      if ((eh === 24 || (eh === 0 && em === 0)) && sh >= 12) return true
      // start > end означает переход через полночь
      const sMin = sh * 60 + (sm || 0)
      const eMinRaw = eh * 60 + (em || 0)
      return eMinRaw < sMin && eMinRaw !== 0
    },
    async createCarryOverForTomorrow() {
      const candidates = (this.rows || []).filter(r =>
        !r.__section && r.effectiveSection !== 'carry' && this._isCrossMidnight(r)
      )
      if (!candidates.length) return 0

      // Определяем завтрашнюю дату (ISO)
      const base = this.planDateISO ? new Date(this.planDateISO + 'T00:00:00') : new Date()
      base.setDate(base.getDate() + 1)
      const pad = (n) => String(n).padStart(2, '0')
      const tomorrowISO = `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`

      // Получаем/создаём chpo на завтра
      const chpoCtx = await checkOrCreateChpoByDate(tomorrowISO)
      const tomorrowChpoId = chpoCtx?.id || chpoCtx?.chpo?.id
      if (!tomorrowChpoId) return 0

      let created = 0
      for (const r of candidates) {
        try {
          const payload = {
            chpo: tomorrowChpoId,
            chief_id: this.stpnIdFromComposition(r) || null,
            user_ids: this.otherIdsFromComposition(r),
            order_type: 'Переходящие наряды',
            squad_type: r.dutyType || '',
            reinforcement_means: (r.reinforcement || []).join(','),
            sector: (r.sectors || []).join(','),
            start_time: r.start_time || r.startTime || null,
            end_time: r.end_time || r.endTime || null,
          }
          await createChpoOrderToSquad(payload)
          created++
        } catch (e) {
          console.warn('carry-over row failed', e)
        }
      }
      return created
    },
    stpnIdFromComposition(row) {
      for (const k of (row.composition || [])) {
        const parsed = this.parsePersonKey(k)
        if (parsed.kind === 'S') return parsed.id
      }
      return null
    },
    otherIdsFromComposition(row) {
      const ids = []
      for (const k of (row.composition || [])) {
        const parsed = this.parsePersonKey(k)
        if (parsed.kind !== 'S' && parsed.id != null) ids.push(parsed.id)
      }
      return ids
    },
    goBack() {
      if (window.history.length > 1) window.history.back(); else this.$router.push('/chpo/brief')
    },
    effectiveSection(r) {
      // Приоритет: явный section -> carryOver -> orderType маппинг -> current
      if (r.section) return r.section;
      if (r.carryOver) return 'carry';
      const ot = (r.orderType || '').trim();
      switch (ot) {
        case 'Переходящие наряды': return 'carry';
        case 'Охрана НСОУ': return 'current';
        case 'Охрана остального участка': return 'sec3';
        case 'Охрана подразделения': return 'sec4';
        case 'Приказ №1': return 'sec5';
      }
      return 'current';
    },
    sectionLabel(key) {
      const def = this.sectionDefs.find(s => s.key === key)
      if (!def) return ''
      return def.labelKey ? this.$t(def.labelKey) : (def.label || '')
    },
    parseReinforcementKeys(str) {
      if (!str || typeof str !== 'string') return []
      return str.split(',').map(s => s.trim()).filter(Boolean)
    },
    computeOrderType(row) {
      // Если строка привязана к явной секции, не даём dutyType менять её "тип приказа"
      if (row.section) {
        switch (row.section) {
          case 'carry': return 'Переходящие наряды';
          case 'current': return 'Охрана НСОУ';
          case 'sec3': return 'Охрана остального участка';
          case 'sec4': return 'Охрана подразделения';
          case 'sec5': return 'Приказ №1';
          default: return row.orderType || 'Охрана НСОУ';
        }
      }
      if (row.carryOver) return 'Переходящие наряды';
      const dt = (row.dutyType || '').trim();
      if (['ППП', 'ПРП', 'Охрана НСОУ'].includes(dt)) return 'Охрана НСОУ';
      if (dt === 'Охрана остального участка') return 'Охрана остального участка';
      if (dt === 'Охрана подразделения') return 'Охрана подразделения';
      if (dt === 'Другие задачи') return 'Приказ №1';
      // Для прочих видов не переопределяем orderType, чтобы строка не прыгала секциями
      return row.orderType || 'Охрана НСОУ';
    },
    toHHMM(val) {
      if (!val) return ''
      const s = String(val).trim()
      const parts = s.split(':')
      if (parts.length >= 2) return `${parts[0].padStart(2,'0')}:${parts[1].padStart(2,'0')}`
      return s
    },
    toHHMMSS(val) {
      if (!val) return ''
      const s = String(val).trim()
      if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s
      if (/^\d{1,2}:\d{2}$/.test(s)) {
        const [h, m] = s.split(':')
        return `${h.padStart(2,'0')}:${m.padStart(2,'0')}:00`
      }
      return s
    },
  }
}
</script>

<style scoped>
.page {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.page__title { margin: 0; color: #111827; font-size: 24px; font-weight: 700; line-height: 33.6px; }
.page__table { width: 100%; }


.cell-text { flex: 1 1 0; color: #111827; font-size: 16px; line-height: 20px; width: 100%; text-align: center; }
.text-muted { color: #6B7280; }
.dup-1 { text-decoration: underline; }
.dup-2 { text-decoration: underline; text-decoration-style: double; }


.time-range { display: inline-flex; align-items: center; gap: 10px; position: relative; z-index: 6; flex-wrap: wrap; }
.time-field { display: inline-flex; align-items: center; gap: 6px; }
.time-field input { min-width: 110px; position: relative; z-index: 6; }
.time-field span { margin-right: 6px; }


.sector-picked { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sector-text { font-weight: 600; }
.sector-cell { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: center; }
.sector-cell--edit { flex-direction: column; align-items: stretch; gap: 6px; width: 100%; }


.actions { display: inline-flex; align-items: center; gap: 10px; justify-content: flex-end; width: 100%; }
.actions__btn { width: 30px; height: 30px; padding: 6px; border-radius: 8px; border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.actions__icon { width: 16px; height: 16px; display: block; }
.actions__btn--edit    { background: #2F78EE; }
.actions__btn--accept  { background: #2A8A1B; }
.actions__btn--delete { background: #D81717; }

.page__footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page__footer-right { display: inline-flex; gap: 12px; margin-left: auto; }


/* Улитка-координата в колонке плана охраны */
.ulitka-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: #FEE2E2;
  color: #991B1B;
  font-family: 'Menlo','Courier New',monospace;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.4px;
}
.ulitka-input {
  width: 100%;
  max-width: 180px;
  padding: 4px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-family: 'Menlo','Courier New',monospace;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  text-align: center;
  box-sizing: border-box;
}
.ulitka-input:focus {
  outline: 2px solid rgba(220,38,38,.25);
  border-color: #DC2626;
}

.aggregation {
  margin-top: 20px;
  padding: 20px 24px;
  background: #F9FAFB;
  border: 2px solid #374151;
  border-radius: 12px;
}
.aggregation__title {
  font-size: 18px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.aggregation__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}
.aggregation__table thead th {
  background: #E5E7EB;
  color: #111827;
  padding: 10px 12px;
  text-align: center;
  font-weight: 700;
  border: 1px solid #9CA3AF;
}
.aggregation__table tbody td {
  padding: 10px 12px;
  text-align: center;
  border: 1px solid #D1D5DB;
  background: #fff;
}
.aggregation__row-label {
  text-align: left !important;
  font-weight: 600;
  color: #374151;
  background: #F3F4F6 !important;
}
.aggregation__total {
  background: #FEF3C7 !important;
  color: #78350F;
  font-size: 16px;
}
.aggregation__row--highlight td {
  background: #DBEAFE !important;
  color: #1E3A8A;
  font-weight: 600;
}
.aggregation__row--highlight .aggregation__row-label {
  background: #BFDBFE !important;
}
.aggregation__row--highlight .aggregation__total {
  background: #93C5FD !important;
  color: #1E3A8A;
}
.aggregation__hint {
  margin-top: 10px;
  color: #6B7280;
  font-size: 13px;
  font-style: italic;
}

.readonly-banner {
  margin-top: 16px;
  padding: 14px 20px;
  background: #EFF6FF;
  border: 2px solid #2563EB;
  border-radius: 12px;
  color: #1E3A8A;
  font-size: 15px;
  font-weight: 500;
}
.warnings { margin-top: 16px; border: 2px solid #E03137; border-radius: 12px; padding: 16px 20px; background: #FFF5F5; }
.warnings__title { font-weight: 800; color: #E03137; margin-bottom: 10px; font-size: 18px; }
.warnings__list { margin: 0; padding-left: 22px; color: #7F1D1D; font-size: 16px; line-height: 1.6; }
.warnings__list li { margin-bottom: 4px; }



.page__table :deep(.tbl__cell) { padding: 12px; }

.page__table :deep(.tbl__cell):has(.actions) { display: flex; align-items: center; justify-content: flex-end; }


.page__table :deep(.tbl__cell) { overflow: visible; }
.not-included {
  margin-top: 20px;
  padding: 16px 20px;
  background: #FFF7ED;
  border: 1px solid #FED7AA;
  border-radius: 12px;
}
.not-included__title {
  color: #9A3412;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.not-included__count {
  background: #EA580C;
  color: #fff;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 13px;
  font-weight: 700;
}
.not-included__list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px 16px;
  color: #7C2D12;
  font-size: 14px;
  line-height: 1.4;
}
.not-included__item {
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #FED7AA;
  border-radius: 6px;
}

.section-row {
  position: relative;
  width: 100%;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 0 12px;
}
.section-row__title {
  flex: 1;
  text-align: center;
  color: #111827;
  font-size: 16px;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 700;
  line-height: 19.20px;
  word-wrap: break-word;
}
.section-row__add {
  flex-shrink: 0;
  margin-left: 12px;
  width: 30px; height: 30px;
  border: 0; border-radius: 8px; background: #2A8A1B; color: #fff; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 18px; line-height: 1;
}

.reinforce-picker { width: 100%; }

.plan-view {
  font-family: 'Times New Roman', Times, serif;
}

/* Toast Notifications */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  min-width: 220px;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}
.toast-success { background: #2A8A1B; }
.toast-error { background: #D81717; }
.toast-content {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
}
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}
.toast-slide-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.toast-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.mpo-alert {
  margin-bottom: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: #fef3c7;
  border-left: 3px solid #d97706;
  color: #92400e;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 4px;
  line-height: 1.3;
}
</style>
